import {
  AsYouType,
  CountryCode,
  formatNumber,
  ParsedNumber,
  parseNumber,
} from 'libphonenumber-js';
import isEmpty from 'lodash/isEmpty';
import toUpper from 'lodash/toUpper';

import { COUNTRY_CODES } from '@savgroup-front-common/constants';

/**
 * Validates if a country code is supported by the application
 * @param countryCode - The country code to validate
 * @returns True if the country code is supported
 */
const isValidSupportedCountryCode = (countryCode: string): boolean => {
  if (!countryCode) {
    return false;
  }

  const upperCountryCode = toUpper(countryCode);

  return Object.values(COUNTRY_CODES).includes(
    upperCountryCode as COUNTRY_CODES,
  );
};

/**
 * Validates if a country code is recognized by libphonenumber-js
 * @param countryCode - The country code to validate
 * @returns True if the country code is valid
 */
const isValidCountryCodeForLib = (countryCode: string): boolean => {
  try {
    // Try to parse a dummy number with the country code
    const dummyNumber = '+1234567890';
    const parsed = parseNumber(dummyNumber, countryCode as CountryCode);

    return !isEmpty(parsed);
  } catch {
    return false;
  }
};

/**
 * Formats a phone number with proper country code handling
 * @param phoneNumber - The phone number to format
 * @param countryCode - The country code (defaults to FR)
 * @returns Formatted phone number or original if formatting fails
 */
export const formatPhoneNumber = (
  phoneNumber?: string | null,
  countryCode: string = COUNTRY_CODES.FR,
): string | undefined | null => {
  if (!phoneNumber) {
    return phoneNumber;
  }

  // Validate country code
  const upperCountryCode = toUpper(countryCode);

  // If country code is not supported, try to detect from phone number
  if (!isValidSupportedCountryCode(upperCountryCode)) {
    // Try to parse without country code first
    const parsedWithoutCountry = parseNumber(phoneNumber.replace(/^00/, '+'));

    if (!isEmpty(parsedWithoutCountry)) {
      return formatNumber(
        parsedWithoutCountry as ParsedNumber,
        'INTERNATIONAL',
      );
    }

    // Fallback to FR if detection fails
    countryCode = COUNTRY_CODES.FR;
  }

  // Validate that the country code is recognized by libphonenumber-js
  if (!isValidCountryCodeForLib(countryCode)) {
    // Try to parse without country code
    const parsedWithoutCountry = parseNumber(phoneNumber.replace(/^00/, '+'));

    if (!isEmpty(parsedWithoutCountry)) {
      return formatNumber(
        parsedWithoutCountry as ParsedNumber,
        'INTERNATIONAL',
      );
    }

    // Return original if parsing fails
    return phoneNumber;
  }

  const parsedPhoneNumber = parseNumber(
    phoneNumber.replace(/^00/, '+'),
    countryCode as CountryCode,
  );

  return isEmpty(parsedPhoneNumber)
    ? phoneNumber
    : formatNumber(parsedPhoneNumber as ParsedNumber, 'INTERNATIONAL');
};

/**
 * Formats a phone number as the user types with proper country code handling
 * @param phoneNumber - The phone number to format
 * @param countryCode - The country code
 * @returns Formatted phone number as user types
 */
export const formatPhoneNumberAsYouType = (
  phoneNumber: string,
  countryCode: string,
): string => {
  if (!phoneNumber || !/^(\+|0)([0-9]|\s)/.test(phoneNumber)) {
    return phoneNumber;
  }

  // Validate country code
  const upperCountryCode = toUpper(countryCode);

  // If country code is not supported or invalid, use FR as fallback
  if (
    !isValidSupportedCountryCode(upperCountryCode) ||
    !isValidCountryCodeForLib(upperCountryCode)
  ) {
    countryCode = COUNTRY_CODES.FR;
  }

  const formatter = new AsYouType(countryCode as CountryCode);
  const formattedNumber = formatter.input(phoneNumber.replace(/^00/, '+'));

  return formattedNumber || phoneNumber;
};
